"use client";

import React, { useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { Search, Moon, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";

/**
 * ⚠️ ВАЖНО: этот компонент НЕ импортирует @/lib/dreams/symbols напрямую.
 * Раньше так было — и весь 286KB symbols.ts попадал в client bundle.
 * Теперь parent (RSC `src/app/(public)/diccionario-de-suenos/page.tsx`) строит лёгкий
 * `SymbolMeta` индекс (только поля нужные для списка/поиска) и передаёт props.
 * Полное толкование подгружается на /diccionario-de-suenos/[slug] (тоже server-side).
 */

export interface SymbolMeta {
  slug: string;
  name: string;
  emoji: string;
  category: string;
  summary: string;
  tags: string[];
}

interface Props {
  lunarDay: number;
  lunarGlyph: string;
  lunarPhaseLabel: string;
  symbolOfNight: { slug: string; emoji: string; name: string; tags: string[]; summary: string };
  /** Полный индекс всех символов — только поля для отображения в списке/поиске.
   *  Готовится в server component через map(symbol → меньший объект). */
  symbolsIndex: SymbolMeta[];
  /** Метки категорий для заголовков групп (cat → readable label). */
  categoryLabels: Record<string, string>;
  /** Slug'и популярных символов — для блока «популярные». */
  popularSlugs: string[];
}

// Использует ssr void для линтера
void Badge;

export function SonnikInteractive({
  lunarDay,
  lunarGlyph,
  lunarPhaseLabel,
  symbolOfNight,
  symbolsIndex,
  categoryLabels,
  popularSlugs,
}: Props) {
  void lunarPhaseLabel;
  const t = useTranslations("sonnikInteractive");
  const [query, setQuery] = useState("");

  // Локальный поиск — заменяет server-side searchSymbols. Простая фильтрация
  // по name, slug, tags + category. По 300+ символов работает мгновенно.
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return [] as SymbolMeta[];
    return symbolsIndex.filter((s) =>
      s.name.toLowerCase().includes(q) ||
      s.slug.includes(q) ||
      s.tags.some((t) => t.toLowerCase().includes(q)) ||
      categoryLabels[s.category]?.toLowerCase().includes(q)
    );
  }, [query, symbolsIndex, categoryLabels]);

  const popular = useMemo(
    () => symbolsIndex.filter((s) => popularSlugs.includes(s.slug)),
    [symbolsIndex, popularSlugs],
  );

  const byCategory = useMemo(() => {
    if (query.trim().length > 0) return [];
    return Object.entries(categoryLabels)
      .map(([cat, label]) => ({
        cat,
        label,
        symbols: symbolsIndex.filter((s) => s.category === cat),
      }))
      .filter((g) => g.symbols.length > 0);
  }, [query, symbolsIndex, categoryLabels]);

  return (
    <div className="space-y-8">
      {/* Symbol of the Night */}
      <Link href={`/diccionario-de-suenos/${symbolOfNight.slug}`} className="block group">
        <div className="relative bg-gradient-to-br from-indigo-700 via-violet-700 to-purple-800 rounded-2xl p-6 text-white overflow-hidden hover:shadow-lg transition-shadow">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/3 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/3" />
          <div className="relative">
            <p className="text-violet-300 text-xs font-semibold uppercase tracking-widest mb-4">
              {lunarGlyph} {t("symbolOfNight", { day: lunarDay })}
            </p>
            <div className="flex items-start gap-5">
              <div className="shrink-0 w-20 h-20 bg-white/10 border border-white/20 rounded-2xl flex items-center justify-center shadow-xl">
                <span className="text-4xl">{symbolOfNight.emoji}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold mb-1 group-hover:text-violet-200 transition-colors">
                  {symbolOfNight.name}
                </h2>
                <div className="flex flex-wrap gap-1 mb-3">
                  {symbolOfNight.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="text-xs bg-white/10 rounded-full px-2.5 py-0.5 text-violet-200">{tag}</span>
                  ))}
                </div>
                <p className="text-sm text-white/80 leading-relaxed line-clamp-2">{symbolOfNight.summary}</p>
              </div>
            </div>
            <p className="text-[10px] text-violet-400 mt-4">{t("clickFull")}</p>
          </div>
        </div>
      </Link>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <input
          type="text"
          placeholder={t("searchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-white text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition"
        />
      </div>

      {/* Search results */}
      {query.trim().length > 0 && (
        <div>
          <p className="text-sm text-stone-500 mb-4">
            {results.length === 0 ? t("nothing") : t("found", { n: results.length })}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {results.map((symbol) => (
              <Link
                key={symbol.slug}
                href={`/diccionario-de-suenos/${symbol.slug}`}
                className="flex items-start gap-4 p-4 bg-white border border-stone-200 rounded-2xl hover:shadow-md hover:border-violet-200 transition-all group"
              >
                <span className="text-3xl shrink-0">{symbol.emoji}</span>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-stone-800 group-hover:text-violet-700 transition-colors">{symbol.name}</h3>
                  <p className="text-xs text-stone-500 leading-relaxed mt-0.5 line-clamp-2">{symbol.summary}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {symbol.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="text-[10px] bg-stone-50 border border-stone-100 rounded-full px-2 py-0.5 text-stone-400">{tag}</span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Popular symbols */}
      {query.trim().length === 0 && (
        <>
          <div>
            <h2 className="text-base font-semibold text-stone-800 mb-4 flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500" />
              {t("popular")}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {popular.map((symbol) => (
                <Link
                  key={symbol.slug}
                  href={`/diccionario-de-suenos/${symbol.slug}`}
                  className="flex flex-col items-center gap-2 p-4 bg-white border border-stone-200 rounded-2xl hover:shadow-md hover:border-violet-200 transition-all group text-center"
                >
                  <span className="text-3xl">{symbol.emoji}</span>
                  <span className="text-sm font-semibold text-stone-700 group-hover:text-violet-700 transition-colors">{symbol.name}</span>
                  <span className="text-[10px] text-stone-400 leading-relaxed line-clamp-2">
                    {symbol.tags.slice(0, 2).join(" · ")}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* All symbols by category */}
          {byCategory.map(({ cat, label, symbols }) => (
            <div key={cat}>
              <h2 className="text-base font-semibold text-stone-800 mb-3 flex items-center gap-2">
                <Moon className="w-4 h-4 text-violet-400" />
                {label}
                <span className="text-xs font-normal text-stone-400 ml-1">{symbols.length}</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {symbols.map((symbol) => (
                  <Link
                    key={symbol.slug}
                    href={`/diccionario-de-suenos/${symbol.slug}`}
                    className="flex items-center gap-3 p-3 bg-white border border-stone-200 rounded-xl hover:shadow-md hover:border-violet-200 transition-all group"
                  >
                    <span className="text-2xl shrink-0">{symbol.emoji}</span>
                    <span className="text-xs font-semibold text-stone-700 group-hover:text-violet-700 transition-colors truncate">
                      {symbol.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
